import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApp } from "@/contexts/AppContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettleUpModal = ({ group, onSuccess, onClose }) => {
  const { t, formatAmount } = useApp();
  const [saving, setSaving] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [fromMember, setFromMember] = useState('');
  const [toMember, setToMember] = useState('');
  const [manualAmount, setManualAmount] = useState('');

  const recordSettlement = async (from, to, amount) => {
    const key = from + to;
    setSaving(key);
    try {
      await axios.post(`${API}/groups/${group.id}/settlements`, {
        from_member: from,
        to_member: to,
        amount: parseFloat(amount)
      });
      toast.success(t('settlement_recorded'));
      onSuccess();
    } catch {
      toast.error(t('error'));
    } finally {
      setSaving(null);
    }
  };

  const handleSuggested = (s) => recordSettlement(s.from_member, s.to_member, s.amount);

  const handleManual = () => {
    if (!fromMember || !toMember || !manualAmount || fromMember === toMember) return;
    recordSettlement(fromMember, toMember, manualAmount);
  };

  const suggestions = group.suggested_settlements || [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="settle-up-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl">{t('settle_up')}</DialogTitle>
        </DialogHeader>

        {suggestions.length === 0 ? (
          <div className="py-10 text-center text-zinc-400 text-base">{t('all_settled')}</div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-semibold">{t('suggested_settlements')}</p>
            {suggestions.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-4 border border-zinc-200 rounded-xl bg-white hover:border-zinc-300 transition-all">
                <div>
                  <div className="text-sm">
                    <span className="font-semibold text-zinc-900">{s.from_name}</span>
                    <span className="text-zinc-400 mx-2">{t('pays')}</span>
                    <span className="font-semibold text-zinc-900">{s.to_name}</span>
                  </div>
                  <div className="text-xl font-bold text-zinc-900 mt-0.5">{formatAmount(s.amount)}</div>
                </div>
                <Button
                  data-testid="mark-as-paid-btn"
                  size="sm"
                  onClick={() => handleSuggested(s)}
                  disabled={!!saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs"
                >
                  {saving === s.from_member + s.to_member ? '...' : t('mark_as_paid')}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Manual Settlement */}
        <div className="border-t border-zinc-200 pt-4">
          <button
            onClick={() => setShowManual(!showManual)}
            className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            <ChevronDown size={14} className={`transition-transform duration-200 ${showManual ? 'rotate-180' : ''}`} />
            {t('manual_settlement')}
          </button>

          {showManual && (
            <div className="mt-3 space-y-3">
              <Select value={fromMember} onValueChange={setFromMember}>
                <SelectTrigger className="h-11"><SelectValue placeholder={t('who_pays')} /></SelectTrigger>
                <SelectContent>
                  {group.members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={toMember} onValueChange={setToMember}>
                <SelectTrigger className="h-11"><SelectValue placeholder={t('pays_to')} /></SelectTrigger>
                <SelectContent>
                  {group.members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                type="number"
                min="0"
                value={manualAmount}
                onChange={e => setManualAmount(e.target.value)}
                placeholder={t('amount')}
                className="h-11"
              />
              <Button
                onClick={handleManual}
                disabled={!!saving}
                className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
              >
                {t('record_payment')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettleUpModal;
